import React, { useCallback, useEffect, useRef, useState } from "react";
import { timestampFormatter } from "../../core/utils/timestampFormatter";
import {
  getBusinessWorkflowRuns,
  getBusinessWorkflowStats,
  getWorkflowActionItems,
} from "../businessModeService";
import WorkflowSyncActivityPanel from "./shared/WorkflowSyncActivityPanel";
import {
  getJobStatusLabel,
  getWorkflowLifecycleStatus,
  parseWorkflowTimestampMs,
} from "./shared/workflowStatus";

const STATUS_PENDING = "pending";
const STATUS_DONE = "done";
const STATUS_IGNORE = "ignore";
const ACTION_ITEMS_REFRESH_INTERVAL_MS = 5000;
const TIMESTAMP_MINUTE = { includeSeconds: false };

// Prefer item_id (stable for the row's lifetime) over key (a hash of the note text, which
// changes whenever the note is edited — see actionItemHelpers.js getItemStableId).
const getItemStableId = (item) => {
  const itemId = String(item?.item_id || "").trim();
  if (itemId) return itemId;
  return `${String(item.key || "")}::${String(item.file_id || "")}`;
};

const buildItemsSignature = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((item) =>
      [
        String(item.key || ""),
        String(item.file_id || ""),
        String(item.status || ""),
        String(item.action_item || ""),
        String(item.created_at || ""),
      ].join("|")
    )
    .sort()
    .join("||");
};

const mergeItemsPreservingOrder = (previousItems, nextItems) => {
  if (!Array.isArray(previousItems) || previousItems.length === 0) {
    return nextItems;
  }
  const nextById = new Map(nextItems.map((item) => [getItemStableId(item), item]));
  const merged = [];
  const seenIds = new Set();

  previousItems.forEach((item) => {
    const id = getItemStableId(item);
    const nextItem = nextById.get(id);
    if (!nextItem) return;
    merged.push(nextItem);
    seenIds.add(id);
  });

  nextItems.forEach((item) => {
    const id = getItemStableId(item);
    if (seenIds.has(id)) return;
    merged.push(item);
  });

  return merged;
};

const upsertItemsPreservingOrder = (previousItems, incomingItems) => {
  if (!Array.isArray(previousItems) || previousItems.length === 0) {
    return incomingItems;
  }
  if (!Array.isArray(incomingItems) || incomingItems.length === 0) {
    return previousItems;
  }
  const incomingById = new Map(incomingItems.map((item) => [getItemStableId(item), item]));
  const seenIds = new Set();
  const merged = previousItems.map((item) => {
    const stableId = getItemStableId(item);
    seenIds.add(stableId);
    return incomingById.get(stableId) || item;
  });
  incomingItems.forEach((item) => {
    const stableId = getItemStableId(item);
    if (seenIds.has(stableId)) return;
    merged.push(item);
  });
  return merged;
};

const getLatestCreatedAtCursor = (items, fallbackCursor = "") => {
  let latestTimestampMs = Number.isFinite(Date.parse(fallbackCursor))
    ? Date.parse(fallbackCursor)
    : 0;
  let latestCursor = fallbackCursor;
  items.forEach((item) => {
    const createdAt = String(item?.created_at || "").trim();
    if (!createdAt) return;
    const createdAtMs = Date.parse(createdAt);
    if (!Number.isFinite(createdAtMs)) return;
    if (createdAtMs > latestTimestampMs) {
      latestTimestampMs = createdAtMs;
      latestCursor = createdAt;
    }
  });
  return latestCursor;
};

const isRetryableActionItemsFetchError = (errorMessage) => {
  const normalizedErrorMessage = String(errorMessage || "").toLowerCase();
  return normalizedErrorMessage.includes("failed to fetch primitive action items");
};

/**
 * True when the transform job finished successfully (action items table ready).
 *
 * @example
 * isTransformJobComplete("DONE");
 * // true
 */
const isTransformJobComplete = (status) => {
  const normalizedStatus = String(status || "").trim().toUpperCase();
  return normalizedStatus === "DONE" || normalizedStatus === "SUCCESS";
};

/**
 * Format one instant from workflow / run payloads for the summary eyebrow.
 *
 * @example
 * formatSummaryInstant(1715000000000);
 * // "May 12,2026 7:02 AM" (local timezone)
 */
const formatSummaryInstant = (value) => {
  const ms = parseWorkflowTimestampMs(value);
  if (!ms) return "";
  return timestampFormatter(ms, TIMESTAMP_MINUTE);
};

/**
 * Workflow overview: schedule / last-run status, digest source count, and per-status action-item counts (no per-task copy, no overall total).
 *
 * @example
 * <BusinessWorkflowActionItemsSummary workflow={wf} isInitialRunAutoRefreshing={false} />
 */
const BusinessWorkflowActionItemsSummary = ({
  workflow,
  isInitialRunAutoRefreshing = false,
}) => {
  const workflowId = String(workflow?.workflow_id || "").trim();
  const workflowName = String(workflow?.name || "").trim();
  const transformJobStatus = String(workflow?.job_status || "");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [waitingForPrimitives, setWaitingForPrimitives] = useState(false);
  const [lastRunDetail, setLastRunDetail] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(true);
  const [runInsightError, setRunInsightError] = useState(false);
  const [syncStats, setSyncStats] = useState(null);

  const mountedRef = useRef(true);
  const refreshInFlightRef = useRef(false);
  const itemsSignatureRef = useRef("");
  const latestCreatedAtCursorRef = useRef("");

  const shouldAutoRefreshFirstRun =
    isInitialRunAutoRefreshing &&
    items.length === 0 &&
    !isTransformJobComplete(transformJobStatus);

  const itemsReady = !loading && !waitingForPrimitives;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!workflowId) return undefined;
    let cancelled = false;
    const load = async () => {
      setRunDetailLoading(true);
      setRunInsightError(false);
      try {
        const [runs, stats] = await Promise.all([
          getBusinessWorkflowRuns(workflowId, 1),
          getBusinessWorkflowStats(workflowId).catch(() => null),
        ]);
        if (cancelled) return;
        if (!cancelled) {
          setSyncStats(stats);
        }
        if (!Array.isArray(runs) || runs.length === 0) {
          setLastRunDetail(null);
          return;
        }
        const latest = runs[0];
        const payloadError = String(latest.payload_error || "").trim();
        const sourceCount =
          !payloadError && stats?.last_transform_job
            ? stats.last_transform_job.sources_in_digest ?? null
            : null;
        if (!cancelled) {
          setLastRunDetail({
            createdAt: latest.created_at,
            digestDate: String(latest.digest_date || ""),
            sourceCount,
            payloadError,
          });
        }
      } catch {
        if (!cancelled) {
          setLastRunDetail(null);
          setRunInsightError(true);
        }
      } finally {
        if (!cancelled) setRunDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  const loadActionItems = useCallback(
    async ({ silent = false } = {}) => {
      if (!workflowId || refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const sinceCreatedAtCursor = silent ? latestCreatedAtCursorRef.current : "";
        const data = await getWorkflowActionItems(workflowId, {
          sinceCreatedAt: sinceCreatedAtCursor,
        });
        if (!mountedRef.current) return;
        const nextItems = Array.isArray(data) ? data : [];
        if (!silent) {
          const nextSignature = buildItemsSignature(nextItems);
          if (itemsSignatureRef.current !== nextSignature) {
            itemsSignatureRef.current = nextSignature;
            setItems((previousItems) => mergeItemsPreservingOrder(previousItems, nextItems));
          }
        } else if (nextItems.length > 0) {
          setItems((previousItems) => upsertItemsPreservingOrder(previousItems, nextItems));
        }
        latestCreatedAtCursorRef.current = getLatestCreatedAtCursor(
          nextItems,
          latestCreatedAtCursorRef.current
        );
        setWaitingForPrimitives(false);
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage = String(err?.message || "Failed to load action items");
        const retryableError = isRetryableActionItemsFetchError(errorMessage);
        if (retryableError) {
          setWaitingForPrimitives(true);
        } else if (!silent) {
          setError(errorMessage);
        }
      } finally {
        refreshInFlightRef.current = false;
        if (mountedRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    [workflowId]
  );

  useEffect(() => {
    loadActionItems({ silent: false });
  }, [loadActionItems]);

  useEffect(() => {
    if (!workflowId || !shouldAutoRefreshFirstRun) return undefined;
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      loadActionItems({ silent: true });
    }, ACTION_ITEMS_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [workflowId, loadActionItems, shouldAutoRefreshFirstRun]);

  if (!workflowId) {
    return null;
  }

  const lifecycle = getWorkflowLifecycleStatus(workflow);
  const jobNorm = transformJobStatus.toUpperCase();
  const isFailed = jobNorm === "FAILED";
  const isRunning = jobNorm === "RUNNING";
  const isDisabled = workflow.active === false;

  let statusDotClass = "workflow-action-summary__status-dot--ok";
  let statusHeadline = "Active";
  if (isDisabled) {
    statusDotClass = "workflow-action-summary__status-dot--muted";
    statusHeadline = "Disabled";
  } else if (isFailed) {
    statusDotClass = "workflow-action-summary__status-dot--error";
    statusHeadline = "Issue";
  } else if (isRunning) {
    statusDotClass = "workflow-action-summary__status-dot--run";
    statusHeadline = "Running";
  }

  const lastRunInstantMs = parseWorkflowTimestampMs(
    lastRunDetail?.createdAt || workflow.last_synced_at
  );
  const eyebrowLastRun = lastRunInstantMs
    ? formatSummaryInstant(lastRunInstantMs)
    : "No completed run yet";

  const jobHint = getJobStatusLabel(transformJobStatus);
  const frequencyLabel = String(workflow.frequency || "").trim() || "scheduled";

  // No total task count here: it included archived items, which misled. Only the
  // per-status tiles below (Open / Done / Archived / AI suggested) are shown.
  let runStatsSentence = "";
  if (runDetailLoading) {
    runStatsSentence = "Loading last digest details…";
  } else if (runInsightError) {
    runStatsSentence = "Could not load last digest details.";
  } else if (lastRunDetail?.payloadError) {
    runStatsSentence = "Last digest payload could not be loaded.";
  } else if (lastRunDetail && lastRunDetail.sourceCount != null) {
    runStatsSentence = `${lastRunDetail.sourceCount} source${
      lastRunDetail.sourceCount === 1 ? "" : "s"
    } in last digest`;
  } else if (!lastRunDetail) {
    runStatsSentence = "No digest history yet — the first run will appear here.";
  }

  const pendingItems = items.filter((i) => i.status === STATUS_PENDING);
  const doneItems = items.filter((i) => i.status === STATUS_DONE);
  const ignoredItems = items.filter((i) => i.status === STATUS_IGNORE);
  const suggestedPending = pendingItems.filter((i) => i.suggested_done);

  const metrics = [
    { key: "open", value: pendingItems.length, label: "Open", hint: "pending tasks" },
    { key: "done", value: doneItems.length, label: "Done", hint: "marked complete" },
    { key: "ignored", value: ignoredItems.length, label: "Archived", hint: "removed from open tasks" },
    {
      key: "suggested",
      value: suggestedPending.length,
      label: "AI suggested",
      hint: "pending with a completion hint",
    },
  ];

  const displayName = workflowName || "This workflow";

  return (
    <div className="workflow-action-summary">
      {error ? <div className="error-message">{error}</div> : null}

      <p className="workflow-action-summary__eyebrow">
        Workflow · {frequencyLabel} · Last run {eyebrowLastRun}
      </p>

      <div className="workflow-action-summary__status-bar">
        <span className={statusDotClass} aria-hidden="true" />
        <div className="workflow-action-summary__status-body">
          <p className="workflow-action-summary__status-line">
            <span className="workflow-action-summary__status-strong">{statusHeadline}</span>
            {lifecycle.primary ? (
              <>
                {" "}
                — <span className="workflow-action-summary__status-em">{lifecycle.primary}</span>
              </>
            ) : null}
            {lifecycle.secondary ? (
              <span className="workflow-action-summary__status-sub"> · {lifecycle.secondary}</span>
            ) : null}
            {jobHint && (isFailed || isRunning) ? (
              <span className="workflow-action-summary__status-sub"> · {jobHint}</span>
            ) : null}
          </p>
          {runStatsSentence ? (
            <p className="workflow-action-summary__status-meta">{runStatsSentence}</p>
          ) : null}
          {lifecycle.showProgress ? (
            <div className="business-workflow-status-progress workflow-action-summary__progress" aria-hidden="true">
              <div
                className="business-workflow-status-progress__fill"
                style={{
                  width: `${Math.min(Math.max(lifecycle.progressPercent, 0), 100)}%`,
                }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {shouldAutoRefreshFirstRun ? (
        <div
          className="business-inline-spinner workflow-action-summary__refresh-hint"
          role="status"
          aria-live="polite"
        >
          <span className="business-inline-spinner__icon" aria-hidden="true" />
          <span>Refreshing task counts…</span>
        </div>
      ) : null}

      <p className="business-helper-text workflow-action-summary__lead">
        {displayName} — numbers only here; open Action Items for full task text, meeting context, and AI
        drafts.
      </p>

      {!itemsReady ? (
        <div className="workflow-action-summary__metrics-loading">
          <div className="business-inline-spinner" role="status" aria-live="polite">
            <span className="business-inline-spinner__icon" aria-hidden="true" />
            <span>{waitingForPrimitives ? "Preparing action items…" : "Loading task counts…"}</span>
          </div>
        </div>
      ) : (
        <div className="workflow-action-summary__metrics">
          {metrics.map(({ key, value, label, hint }) => (
            <div key={key} className="workflow-action-summary__metric" aria-label={`${label}: ${value}`}>
              <span className="workflow-action-summary__metric-value">{value}</span>
              <span className="workflow-action-summary__metric-label">{label}</span>
              <span className="workflow-action-summary__metric-hint">{hint}</span>
            </div>
          ))}
        </div>
      )}

      {syncStats || runDetailLoading ? (
        <WorkflowSyncActivityPanel
          syncStats={syncStats}
          loading={runDetailLoading && !syncStats}
          className="workflow-action-summary__sync-activity"
        />
      ) : null}
    </div>
  );
};

export default BusinessWorkflowActionItemsSummary;
